import { defineStore } from 'pinia'

import { StoreId } from '@/store'
import { useAnalyticsStore } from '@/store/analytics/analytics'
import { ToggleState } from '@/store/toggle/toggle.types'

import {
  SESSION_ID_COOKIE,
  VARIOQUB_USER_ID_COOKIE,
} from '@/api/modules/user/types'
import { Success } from '@/api/results'

import type { VarioqubPayload } from '@/api/modules/city/types'

export const useToggleStore = defineStore(StoreId.TOGGLE, {
  state: (): ToggleState => ({
    featureToggle: {
      basket_enabled: false,
      new_catalog_enabled: false,
      carrotquest_enabled: false,
      carrotquest_mobile_chat_enabled: false,
      carrotquest_site_chat_enabled: false,
      halves_enabled: false,
      auth_by_vk_id_enabled: false,
      basket_order_checkout_do_not_call_me_back_enabled: false,
      stories_enabled: false,
      address_v4_enabled: false,
      product_card_enabled: false,
      outsource_game_enabled: true,
      new_address_flow_enabled: false,
      is_landing_page_enabled: true,
      menu_by_points_enabled: false,
      varioqub_enabled: false,
    },
    varioqubData: null,
    isLoaded: false,
  }),
  actions: {
    setState(newFeatureToggleState: Partial<ToggleState>) {
      this.featureToggle = Object.assign(
        this.featureToggle,
        newFeatureToggleState,
      )
    },
    async getFeatureToggles() {
      if (this.isLoaded) {
        return
      }

      const { $apiService } = useNuxtApp()

      const result = await $apiService.city.fetchFeatureToggle()

      this.isLoaded = true

      if (result instanceof Success) {
        this.featureToggle = Object.assign(this.featureToggle, result.data)
      }
    },

    async getVarioqub() {
      const { yandexAnalyticsProps } = useAnalyticsStore()

      if (this.varioqubData || !yandexAnalyticsProps.main_id) {
        return
      }

      const origin = useRequestURL().origin
      const userIdCookie = useCookie(VARIOQUB_USER_ID_COOKIE)
      const sessionid = useCookie(SESSION_ID_COOKIE).value || ''

      const payload: VarioqubPayload = {
        client_id: `metrika.${yandexAnalyticsProps.main_id}`,
        url: origin,
        i: userIdCookie.value || '',
        cuid: sessionid,
      }

      const { $apiService } = useNuxtApp()
      const result = await $apiService.city.fetchVarioqub(payload)

      if (!(result instanceof Success)) return

      // Если нет идентификатор посетителя сайта, то запишем в него значение из ответа
      if (!userIdCookie.value) {
        userIdCookie.value = result.data.i
      }

      this.varioqubData = result.data
    },
  },
  getters: {
    isVisibleStories: ({ featureToggle }) => featureToggle.stories_enabled,
    // Включены ли новая главная страница и каталог
    newCatalogEnabled: ({ featureToggle }) =>
      featureToggle?.new_catalog_enabled,
    basketEnabled: ({ featureToggle }) => featureToggle.basket_enabled,
    newAddressFlowEnabled: ({ featureToggle }) =>
      featureToggle.new_address_flow_enabled,
    isLandingPageEnabled: ({ featureToggle }) =>
      featureToggle.is_landing_page_enabled,
    menuByPointsEnabled: ({ featureToggle }) =>
      featureToggle.menu_by_points_enabled,
    varioqubToggles: ({ varioqubData }) => {
      if (!varioqubData || !varioqubData.flags?.length) return {}

      const parseValue = (value: any) => {
        if (value === 'true') return true

        if (value === 'false') return false

        if (!isNaN(value) && value.trim() !== '') return Number(value)

        return value
      }

      const result: Record<string, boolean | number | string> = {}

      return varioqubData.flags.reduce((acc, item) => {
        acc[item.n] = parseValue(item.v)
        return acc
      }, result)
    },
  },
})
